/**
 * Shared dialog for naming a new pipeline stage.
 * Persistence stays with the caller (createPipelineStage mutation).
 */

import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/atoms/Button';
import { Input } from '@/components/ui/atoms/Input';
import { Dialog } from '@/components/ui/organisms/Dialog';

export interface CreatePipelineStageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  onLabelChange: (label: string) => void;
  onConfirm: (label: string) => void;
  isSubmitting?: boolean;
}

export function CreatePipelineStageDialog({
  open,
  onOpenChange,
  label,
  onLabelChange,
  onConfirm,
  isSubmitting = false,
}: CreatePipelineStageDialogProps) {
  const { t } = useTranslation(['job', 'common']);
  const trimmed = label.trim();
  const title = t('job:pipeline.create_stage_title', {
    defaultValue: 'Create stage',
  });

  const confirm = () => {
    if (!trimmed || isSubmitting) return;
    onConfirm(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={title} maxWidth="sm">
      <Dialog.Header title={title} showCloseButton />
      <Dialog.Body className="!overflow-visible">
        <Input
          data-autofocus=""
          value={label}
          onChange={(event) => onLabelChange(event.target.value)}
          placeholder={t('job:pipeline.stage_name_placeholder', {
            defaultValue: 'Stage name',
          })}
          autoFocus
          disabled={isSubmitting}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              confirm();
            }
          }}
        />
      </Dialog.Body>
      <Dialog.Footer align="right">
        <Button
          type="outlined"
          intent="secondary"
          size="sm"
          label={t('common:actions.cancel', { defaultValue: 'Cancel' })}
          disabled={isSubmitting}
          onClick={() => onOpenChange(false)}
        />
        <Button
          type="contained"
          intent="primary"
          size="sm"
          label={t('job:pipeline.create_stage_confirm', {
            defaultValue: 'Create',
          })}
          disabled={!trimmed || isSubmitting}
          loading={isSubmitting}
          onClick={confirm}
        />
      </Dialog.Footer>
    </Dialog>
  );
}
